import { useEffect, useRef, useState } from 'react'
import { Music2 } from 'lucide-react'
import { soundsApi, type SoundEffect } from '../../services/api'
import { playSoundBroadcast } from '../../services/soundboard'

// Bottom-anchored popover that mimics Discord's soundboard tray.
// Lists every sound the admin has uploaded, grouped by category, and
// fires playSoundBroadcast on click so every participant in the voice
// room hears the same clip.
export default function SoundboardPicker() {
  const [open, setOpen] = useState(false)
  const [sounds, setSounds] = useState<SoundEffect[]>([])
  const [loading, setLoading] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (!ref.current) return
      if (!ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  useEffect(() => {
    if (!open) return
    setLoading(true)
    soundsApi
      .list()
      .then((res) => setSounds(res.data))
      .catch(() => setSounds([]))
      .finally(() => setLoading(false))
  }, [open])

  // Group by category, putting "uncategorised" last under a generic
  // header. Stable order within a category — server already sorts by
  // createdAt asc.
  const grouped = sounds.reduce<Record<string, SoundEffect[]>>((acc, s) => {
    const key = s.category || '— Other —'
    if (!acc[key]) acc[key] = []
    acc[key].push(s)
    return acc
  }, {})
  const categories = Object.keys(grouped)

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        title="Soundboard"
        className={`w-10 h-10 rounded-full flex items-center justify-center transition duration-150 ${
          open ? 'bg-primary/20 text-primary' : 'bg-muted text-foreground hover:bg-muted/80'
        }`}
      >
        <Music2 size={20} />
      </button>
      {open && (
        <div className="absolute bottom-full mb-2 right-0 w-72 max-h-96 overflow-y-auto bg-card rounded-lg ring-1 ring-border shadow-lg z-30">
          <div className="px-3 py-2 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider border-b border-border sticky top-0 bg-card">
            Soundboard
          </div>
          {loading && (
            <div className="px-3 py-4 text-xs text-muted-foreground">Loading…</div>
          )}
          {!loading && sounds.length === 0 && (
            <div className="px-3 py-4 text-xs text-muted-foreground">
              No sounds yet. Admins can upload via the admin panel.
            </div>
          )}
          {!loading && categories.map((cat) => (
            <div key={cat}>
              <div className="px-3 pt-2 pb-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                {cat}
              </div>
              <div className="grid grid-cols-2 gap-1 px-2 pb-2">
                {grouped[cat].map((s) => (
                  <button
                    key={s.id}
                    onClick={() => playSoundBroadcast(s.fileUrl, s.name)}
                    className="flex items-center gap-2 px-2 py-2 rounded-md hover:bg-accent text-left transition"
                    title={s.name}
                  >
                    <span className="text-lg shrink-0">{s.emoji || '🔊'}</span>
                    <span className="text-xs truncate flex-1">{s.name}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
